// src/modules/app-views/crm/pages/Document/tabs/FollowUpTab/index.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useMemo, useCallback, useState } from 'react';
import { Button, Card, Flex, Grid, Splitter, theme } from 'antd';
import { SaveOutlined } from '@ant-design/icons';
import { useDocumentContext } from 'components/core/DocumentProvider';
import { useModuleContext } from 'components/core/ModuleProvider';
import useZustand from 'hooks/useZustand';
import translateX from 'utils/translateX';
import { getStorageModuleItem, setStorageModuleItem } from 'stores/module';
import FormFieldsRenderer from '../../../../components/FormFieldsRenderer';
import FollowUpPanel from './FollowUpPanel';
import CustomerIntelligencePanel from '../../../../../omnichannel/pages/Init/components/CustomerIntelligencePanel';
import KanbanCard from '../../../Kanban/components/KanbanCard';

// *******************************************************************************
// CONSTANTS
// *******************************************************************************
const { useBreakpoint } = Grid;
const SHOW_INTELLIGENCE_PANEL = false;
const DEFAULT_FORM_PANEL_WIDTH = 240;
const DEFAULT_INTELLIGENCE_PANEL_WIDTH = 380;
const MIN_INTELLIGENCE_PANEL_WIDTH = 200;
const MAX_INTELLIGENCE_PANEL_WIDTH = '40%';
const STORAGE_KEY_FORM_PANEL = 'crmFollowUpFormPanelWidth';
const STORAGE_KEY_INTELLIGENCE_PANEL = 'crmFollowUpIntelligencePanelWidth';
const STORAGE_KEY_INTELLIGENCE_VISIBLE = 'crmFollowUpIntelligencePanelVisible';

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const FollowUpTab = () => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const { data } = useDocumentContext();
  const { formContext } = useModuleContext();
  const storedParameters = useZustand('app', 'parameters');

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. HOOKS & REFS
  // ─────────────────────────────────────────────────────────────────────────────
  const screens = useBreakpoint();
  const { token } = theme.useToken();

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. LOCAL VARIABLES I
  // ─────────────────────────────────────────────────────────────────────────────
  const isMobile = !screens.sm;
  const isDealClosed = data?.status === 'contract_closed' || data?.status === 'lost';
  const storedFormPanelWidth = getStorageModuleItem(STORAGE_KEY_FORM_PANEL);
  const storedIntelligencePanelWidth = getStorageModuleItem(STORAGE_KEY_INTELLIGENCE_PANEL);
  // null = never stored before → default visible; stored value is boolean
  const storedIntelligenceVisible = getStorageModuleItem(STORAGE_KEY_INTELLIGENCE_VISIBLE);

  const boardUid = data?.boardUid;
  const columnUid = data?.columnUid;
  const boards = storedParameters?.modules?.crm?.boards || [];
  const board = boards.find(b => b.uid === boardUid);
  const column = board?.columns?.find(c => c.uid === columnUid) || null;
  const columnSla = column?.automation?.sla || null;

  // Normalize document data to the flat field paths KanbanCard expects.
  // DocumentProvider nests attendance under data.attendance.* and filed under
  // data._metadata.audit.filedAt, while the kanban board projection exposes them flat.
  const cardData = useMemo(() => {
    if (!data) return null;
    return {
      ...data,
      filedAt: data?.filedAt ?? data?._metadata?.audit?.filedAt ?? null,
      attendanceStartedAt: data?.attendanceStartedAt ?? data?.attendance?.startedAt ?? null,
      attendanceRescheduledAt: data?.attendanceRescheduledAt ?? data?.attendance?.rescheduledAt ?? null,
      attendanceRescheduledTo: data?.attendanceRescheduledTo ?? data?.attendance?.rescheduledTo ?? null,
    };
  }, [data]);

  const formFields = useMemo(() => board?.formFields || [], [board]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const [isFullscreen, setIsFullscreen] = useState(false);
  // null means never stored → show by default; false = user hid it explicitly
  const [intelligenceVisible, setIntelligenceVisible] = useState(storedIntelligenceVisible !== false);

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. EFFECTS
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 6. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  const handleSplitterResize = useCallback(sizes => {
    // sizes[0] = form panel, sizes[2] = intelligence panel
    if (sizes?.[0]) {
      setStorageModuleItem(STORAGE_KEY_FORM_PANEL, Math.round(sizes[0]));
    }
    if (sizes?.[2] > 0) {
      setStorageModuleItem(STORAGE_KEY_INTELLIGENCE_PANEL, Math.round(sizes[2]));
    }
  }, []);

  // Fired by the built-in AntD collapsible arrow on the divider
  // collapsed is boolean[] for each panel; index 2 = intelligence panel
  const handleSplitterCollapse = useCallback(collapsed => {
    const isCollapsed = !!collapsed[2];
    setIntelligenceVisible(!isCollapsed);
    setStorageModuleItem(STORAGE_KEY_INTELLIGENCE_VISIBLE, !isCollapsed);
  }, []);

  const handleToggleFullscreen = useCallback(() => {
    setIsFullscreen(prev => !prev);
  }, []);

  // ─────────────────────────────────────────────────────────────────────────────
  // 7. LOCAL VARIABLES II
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 8. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <>
      {/* Override stacking contexts so the fullscreen splitter floats above TopBar */}
      {isFullscreen && (
        <style>{`
          .ant-layout-content { z-index: 1000 !important; }
          #server-wrapper-content { z-index: 1000 !important; }
        `}</style>
      )}

      <div
        style={
          isFullscreen
            ? {
                position: 'fixed',
                inset: 0,
                zIndex: 1000,
                background: token.colorBgContainer,
                overflow: 'hidden',
                transition: 'all 0.3s ease',
              }
            : undefined
        }
      >
        <Splitter
          orientation={isMobile ? 'vertical' : 'horizontal'}
          style={{ minHeight: isMobile ? undefined : 480, height: isFullscreen ? '100vh' : undefined }}
          onResize={handleSplitterResize}
          onCollapse={handleSplitterCollapse}
        >
          {/* LEFT panel: form fields (1 per row, vertical) */}
          <Splitter.Panel
            defaultSize={storedFormPanelWidth || DEFAULT_FORM_PANEL_WIDTH}
            min={160}
            max="50%"
            style={{ overflowY: 'auto' }}
          >
            <div style={{ padding: 16 }}>
              {/* Identity card: only in fullscreen — reuses KanbanCard without actions */}
              {isFullscreen && cardData && (
                <div style={{ marginBottom: 16 }}>
                  <KanbanCard
                    card={cardData}
                    boardUid={boardUid}
                    columnUid={columnUid}
                    columnSla={columnSla}
                    standalone
                  />
                </div>
              )}

              <FormFieldsRenderer formFields={formFields} columnUid={columnUid} />

              <Button
                type="primary"
                icon={<SaveOutlined />}
                block
                disabled={isDealClosed}
                style={{ marginTop: 8 }}
                onClick={() => formContext?.submitForm?.()}
              >
                {translateX('save')}
              </Button>
            </div>
          </Splitter.Panel>

          {/* MIDDLE panel: follow-up chat */}
          <Splitter.Panel min="30%" style={{ overflowY: 'auto' }}>
            <FollowUpPanel isFullscreen={isFullscreen} onToggleFullscreen={handleToggleFullscreen} />
          </Splitter.Panel>

          {/* RIGHT panel: customer intelligence (mock, collapsible) */}
          {SHOW_INTELLIGENCE_PANEL && (
            <Splitter.Panel
              defaultSize={intelligenceVisible ? Math.max(storedIntelligencePanelWidth || DEFAULT_INTELLIGENCE_PANEL_WIDTH, MIN_INTELLIGENCE_PANEL_WIDTH) : 0}
              min={MIN_INTELLIGENCE_PANEL_WIDTH}
              max={MAX_INTELLIGENCE_PANEL_WIDTH}
              collapsible
              style={{ overflowX: 'hidden', overflowY: 'auto' }}
            >
              <CustomerIntelligencePanel isPanelVisible={intelligenceVisible} />
            </Splitter.Panel>
          )}
        </Splitter>
      </div>
    </>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default FollowUpTab;

